// @ts-strict-ignore
import React, { useEffect, useMemo, useState } from 'react'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import {
  ActionListChildren,
  Avatar,
  Box,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSeparator,
  Highlight,
  Icon,
  Tooltip,
} from '@node-space/ui-components'
import { PathNames } from 'constants/General'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { customerModelAtom } from 'pages/Customers/atoms/customersAtoms'
import { getCustomerWalletActions } from 'pages/Customers/helpers/utils'
import { BulkCapabilityResponse } from 'types/capabilities/capabilities'
import {
  CustomerWallet,
  CustomerWalletStatusType,
  LedgerWalletCapabilities,
  OrchestratedCustomerWallet,
  WalletCustodyModel,
} from 'types/customers'
import { WalletLifecycleActionTypes, type WalletLifecycleAction } from 'types/wallets'
import { numberWithCommas } from 'utils/utils'
import { getFiatWalletActionSupport } from 'utils/wallets/fiat'

interface UseCustomerWalletsTableRowDataProps {
  customerWallets: CustomerWallet[]
  isActiveCustomer: boolean
  bulkCapabilities?: BulkCapabilityResponse
  isFetchingCapabilities: boolean
  handleExportTransactionClick: (customerWallet: CustomerWallet) => void
  handleOnDetailsClick: (index: number) => void
  handleOpenMoveSheet: (wallet: CustomerWallet) => void
  handleOpenFiatDepositSheet: (customerWallet: CustomerWallet) => void
  handleOpenSendFiatSheet: (customerWallet: CustomerWallet) => void
  handleOpenSendCryptoSheet: (customerWallet: CustomerWallet) => void
  handleWalletStatusUpdate?: (customerWallet: CustomerWallet, action: WalletLifecycleAction) => void
  handleOpenWalletNameEditSheet: (customerWallet: CustomerWallet) => void
}

export const useCustomerWalletsTableRowData = ({
  bulkCapabilities,
  isFetchingCapabilities,
  customerWallets,
  isActiveCustomer,
  handleExportTransactionClick,
  handleOnDetailsClick,
  handleOpenMoveSheet,
  handleOpenFiatDepositSheet,
  handleOpenSendFiatSheet,
  handleOpenSendCryptoSheet,
  handleWalletStatusUpdate,
  handleOpenWalletNameEditSheet,
}: UseCustomerWalletsTableRowDataProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { viewOnly: isViewOnlyRole } = useProfileContext() || {}
  const { accountCapabilities } = useAccountsContext()
  const { enableCryptoFiatSend } = useFeatureFlags()
  const { canEdit: canEditCustomers } = useMappedRoles()?.customers || {}

  const customerModel = useAtomValue(customerModelAtom)

  const [tableRows, setTableRows] = useState<ActionListChildren[]>([])

  const handleOnTableRowClick = (customerWallet: CustomerWallet) => {
    void navigate(
      `${PathNames.CUSTOMERS}/${customerWallet?.customerReference}/${customerWallet?.id}`
    )
  }

  const getPayoutActions = ({
    enableCryptoFiatSend,
    isCryptoSendOptionEnabled,
    isUnifiedPayoutOptionEnabled,
    customerWallet,
  }: {
    enableCryptoFiatSend: boolean
    isCryptoSendOptionEnabled: boolean
    isUnifiedPayoutOptionEnabled: boolean
    customerWallet: CustomerWallet
  }) => {
    const { id } = customerWallet || {}

    if (enableCryptoFiatSend) {
      if (isCryptoSendOptionEnabled || isUnifiedPayoutOptionEnabled) {
        return [
          <DropdownMenuItem
            key={`crypto-send-option-${id}`}
            LeadingIcon={<Icon name="ArrowUpRight" />}
            onClick={() => handleOpenSendCryptoSheet(customerWallet)}
          >
            {t('transactionAction.send')}
          </DropdownMenuItem>,
        ]
      }
    }

    return [
      ...(isCryptoSendOptionEnabled
        ? [
            <DropdownMenuItem
              key={`crypto-send-option-${id}`}
              LeadingIcon={<Icon name="ArrowUpRight" />}
              onClick={() => handleOpenSendCryptoSheet(customerWallet)}
            >
              {t('payments.sendCrypto')}
            </DropdownMenuItem>,
          ]
        : []),
      ...(isUnifiedPayoutOptionEnabled
        ? [
            <DropdownMenuItem
              key={`fiat-send-option-${id}`}
              LeadingIcon={<Icon name="CoinArrowOutCustom" />}
              onClick={() => handleOpenSendFiatSheet(customerWallet)}
            >
              {t('wallets.fiatSend.sendFiat')}
            </DropdownMenuItem>,
          ]
        : []),
    ]
  }

  const getActiveCustomerWalletMenuItems = (
    customerWallet: OrchestratedCustomerWallet,
    customerWalletListIndex: number
  ) => {
    const {
      // crypto and fiat
      isCryptoSendOptionEnabled,
      isTransactionExportOptionEnabled,
      // fiat wallet actions
      isUnifiedPayoutOptionEnabled,
      isFiatMoveOptionEnabled,
      isFiatDepositOptionEnabled,
      // crypto wallet actions
      isCryptoReceiveOptionEnabled,
      isCryptoMoveOptionEnabled,
    } = getCustomerWalletActions({
      accountCapabilities,
      customerWallet,
      isViewOnlyRole,
      customerModel,
    })

    const { id } = customerWallet || {}

    const paymentActions = getPayoutActions({
      enableCryptoFiatSend,
      isCryptoSendOptionEnabled,
      isUnifiedPayoutOptionEnabled,
      customerWallet,
    })

    return [
      <DropdownMenuItem
        key={`details-option-${customerWallet?.id}`}
        LeadingIcon={<Icon name="Info" />}
        onClick={() => handleOnDetailsClick(customerWalletListIndex)}
      >
        {t('details')}
      </DropdownMenuItem>,
      ...(!isViewOnlyRole
        ? [
            <DropdownMenuItem
              key={`edit-wallet-name-option-${customerWallet?.id}`}
              LeadingIcon={<Icon name="SimplePencilCustom" />}
              onClick={() => handleOpenWalletNameEditSheet(customerWallet)}
            >
              {t('wallets.editWalletName')}
            </DropdownMenuItem>,
          ]
        : []),
      ...(isTransactionExportOptionEnabled
        ? [
            <DropdownMenuItem
              key={`transaction-export-option-${id}`}
              LeadingIcon={<Icon name="DownloadSimple" />}
              onClick={() => handleExportTransactionClick(customerWallet)}
            >
              {t('export')}
            </DropdownMenuItem>,
          ]
        : []),
      ...(isCryptoReceiveOptionEnabled
        ? [
            <DropdownMenuItem
              key={`crypto-receive-option-${id}`}
              LeadingIcon={<Icon name="ArrowDownLeft" />}
              onClick={() => {
                void navigate(
                  `${PathNames.CUSTOMERS}/${customerWallet?.customerReference}/${customerWallet?.id}?tab=receive-crypto`
                )
              }}
            >
              {t('payments.receiveCrypto')}
            </DropdownMenuItem>,
          ]
        : []),

      ...paymentActions,

      ...(isFiatMoveOptionEnabled || isCryptoMoveOptionEnabled
        ? [
            <DropdownMenuItem
              key={`move-option-${id}`}
              LeadingIcon={<Icon name="ArrowsLeftRight" />}
              onClick={() => handleOpenMoveSheet(customerWallet)}
            >
              {t('customers.move')}
            </DropdownMenuItem>,
          ]
        : []),
      ...(isFiatDepositOptionEnabled
        ? [
            <DropdownMenuItem
              key={`deposit-option-${id}`}
              LeadingIcon={<Icon name="CoinArrowInCustom" />}
              onClick={() => handleOpenFiatDepositSheet(customerWallet)}
            >
              {t('transactionAction.deposit')}
            </DropdownMenuItem>,
          ]
        : []),
    ]
  }

  const getWalletActionMenuItems = (customerWallet: OrchestratedCustomerWallet) => {
    if (isViewOnlyRole || !canEditCustomers) return []

    const { id, status } = customerWallet
    const isActive = status === CustomerWalletStatusType.ACTIVE
    const isInactive = status === CustomerWalletStatusType.INACTIVE

    const walletActionMenuItems: React.ReactElement[] = []

    if (isActive) {
      walletActionMenuItems.push(
        <DropdownMenuItem
          key={`block-option-${id}`}
          LeadingIcon={<Icon name="Lock" />}
          onClick={() =>
            handleWalletStatusUpdate?.(customerWallet, WalletLifecycleActionTypes.BLOCK)
          }
        >
          {t('wallets.actions.block')}
        </DropdownMenuItem>
      )
    }

    if (isInactive) {
      walletActionMenuItems.push(
        <DropdownMenuItem
          key={`unblock-option-${id}`}
          LeadingIcon={<Icon name="Lock" />}
          onClick={() =>
            handleWalletStatusUpdate?.(customerWallet, WalletLifecycleActionTypes.UNBLOCK)
          }
        >
          {t('wallets.actions.unblock')}
        </DropdownMenuItem>
      )
    }

    if (isActive || isInactive) {
      walletActionMenuItems.push(
        <DropdownMenuItem
          key={`terminate-option-${id}`}
          LeadingIcon={<Icon name="Trash" />}
          onClick={() =>
            handleWalletStatusUpdate?.(customerWallet, WalletLifecycleActionTypes.TERMINATE)
          }
        >
          {t('wallets.actions.terminate')}
        </DropdownMenuItem>
      )
    }

    return walletActionMenuItems
  }

  const getCustomerWalletMenuItems = (
    customerWallet: OrchestratedCustomerWallet,
    customerWalletIndex: number
  ) => {
    if (customerWallet?.custodyModel === WalletCustodyModel.SELF_CUSTODIAL) {
      const { isTransactionExportOptionEnabled } = getCustomerWalletActions({
        accountCapabilities,
        customerWallet,
        isViewOnlyRole,
        customerModel,
      })

      return isTransactionExportOptionEnabled
        ? [
            <DropdownMenuItem
              key={`transaction-export-option-${customerWallet?.id}`}
              LeadingIcon={<Icon name="DownloadSimple" />}
              onClick={() => handleExportTransactionClick(customerWallet)}
            >
              {t('export')}
            </DropdownMenuItem>,
          ]
        : []
    }

    const isWalletActive = customerWallet?.status === CustomerWalletStatusType.ACTIVE
    const isWalletTerminated = customerWallet?.status === CustomerWalletStatusType.TERMINATED
    const walletActions = isWalletTerminated ? [] : getWalletActionMenuItems(customerWallet)

    if (isWalletActive && isActiveCustomer) {
      const activeItems = getActiveCustomerWalletMenuItems(customerWallet, customerWalletIndex)
      return [
        ...activeItems,
        ...(walletActions.length
          ? [<DropdownMenuSeparator key="actions-separator" />, ...walletActions]
          : []),
      ]
    }

    const { isTransactionExportOptionEnabled } = getCustomerWalletActions({
      accountCapabilities,
      customerWallet,
      isViewOnlyRole,
      customerModel,
    })

    const informationItems = [
      <DropdownMenuItem
        key={`details-option-${customerWallet?.id}`}
        LeadingIcon={<Icon name="Info" />}
        onClick={() => handleOnDetailsClick(customerWalletIndex)}
      >
        {t('details')}
      </DropdownMenuItem>,
      ...(isTransactionExportOptionEnabled
        ? [
            <DropdownMenuItem
              key={`transaction-export-option-${customerWallet?.id}`}
              LeadingIcon={<Icon name="DownloadSimple" />}
              onClick={() => handleExportTransactionClick(customerWallet)}
            >
              {t('export')}
            </DropdownMenuItem>,
          ]
        : []),
    ]

    return [
      ...informationItems,
      ...(walletActions.length
        ? [<DropdownMenuSeparator key="actions-separator" />, ...walletActions]
        : []),
    ]
  }

  const wallets: OrchestratedCustomerWallet[] = useMemo(() => {
    return customerWallets?.map(customerWallet => {
      const { supportsPay, supportsDeposits, supportsWithdrawals, supportsInternalTransfers } =
        getFiatWalletActionSupport({
          walletProperties: {
            currency: customerWallet?.balance?.currencyCode,
            lsid: customerWallet?.id,
          },
          bulkCapabilities,
        })

      return {
        ...customerWallet,
        supportsDeposits: supportsDeposits,
        supportsWithdrawals: supportsWithdrawals,
        supportsThirdParty: supportsPay,
        supportsInternalTransfers: supportsInternalTransfers,
      }
    })
  }, [bulkCapabilities, customerWallets])

  useEffect(() => {
    if (!isFetchingCapabilities) {
      const rows: ActionListChildren[] = wallets?.map((customerWallet, index) => {
        const { id, balance, status, name, capabilities } = customerWallet || {}
        const { currencyCode, value } = balance || {}

        const isWalletActive = status === CustomerWalletStatusType.ACTIVE
        const customerWalletMenuItems = getCustomerWalletMenuItems(customerWallet, index)
        const isSafeguarded = capabilities?.includes(LedgerWalletCapabilities.SAFEGUARDED)

        return {
          transparent: true,
          key: id,
          id: `wallet-row-${id}`,
          title: name,
          testid: `wallet-row-${id}`,
          ...(isSafeguarded && {
            HeaderHighlightLeft: (
              <Tooltip
                position="right"
                bodyContent={t('wallets.safeguardTooltip')}
                type="complex"
                actionButton={
                  <Button
                    variant="ghost"
                    noPadding
                    onClick={() =>
                      window.open(
                        process.env.SAFEGUARD_LEARN_MORE_LINK,
                        '_blank',
                        'noopener,noreferrer'
                      )
                    }
                  >
                    {t('wallets.safeguardTooltipSeeHere')}
                  </Button>
                }
              >
                <div
                  onMouseEnter={e => e.stopPropagation()}
                  onMouseLeave={e => e.stopPropagation()}
                >
                  <Highlight
                    type="success"
                    variant="emphasis"
                    iconElement={<Icon size="small" name="ShieldCheck" />}
                  >
                    {t('wallets.safeguarded')}
                  </Highlight>
                </div>
              </Tooltip>
            ),
          }),
          ...(!isWalletActive && {
            ActionHighlight: (
              <Box className="pr-2">
                <Highlight
                  type="warning"
                  variant="emphasis"
                  iconElement={<Icon name="WarningCircle" />}
                >
                  {customerWallet?.status}
                </Highlight>
              </Box>
            ),
          }),

          Avatar: (
            <Box paddingL={8}>
              <Box className="relative">
                <Avatar name={currencyCode} />
              </Box>
            </Box>
          ),

          BodyPre: currencyCode?.toLocaleUpperCase(),
          onItemClick: () => {
            handleOnTableRowClick(customerWallet)
          },
          RightPrimaryText: `${numberWithCommas(value ?? 0)} ${currencyCode ?? ''}`,
          // To prevent showing empty dropdown menu
          CustomAction: customerWalletMenuItems?.length ? (
            <DropdownMenu DropdownMenuItems={customerWalletMenuItems}>
              <Button iconElement={<Icon name="DotsThreeVerticalCustom" />} variant="ghost" />
            </DropdownMenu>
          ) : (
            <Button
              disabled={true}
              iconElement={<Icon name="DotsThreeVerticalCustom" />}
              variant="ghost"
            />
          ),
        }
      })

      setTableRows(rows)
    }
  }, [wallets, isFetchingCapabilities, isActiveCustomer])

  return tableRows
}
